import { useCallback, useEffect, useState } from 'react';
import { getTextWidths } from '../features/filters/helpers/filterUtils';
import type {
  TruncatedTermsArgs,
  TruncatedTermsResult,
} from './useTruncatedTerms.typed';

/**
 * Custom hook to truncate term-based descriptions based on container width.
 *
 * Takes terms separated by '·' and fits as many as possible in the container,
 * showing remaining count as (+N) for hidden terms.
 */

const useTruncatedTerms = (args: TruncatedTermsArgs): TruncatedTermsResult => {
  const { description, separator = '·', containerRef } = args;

  const [truncatedText, setTruncatedText] = useState('');

  const updateTruncatedText = useCallback(() => {
    if (!description?.trim()) {
      setTruncatedText('');
      return;
    }

    if (!containerRef.current) {
      setTruncatedText(description);
      return;
    }

    const containerWidth = containerRef.current.offsetWidth;
    if (containerWidth <= 0) {
      setTruncatedText(description);
      return;
    }

    const terms = description.split(separator).map((term) => term.trim());

    if (terms.length === 1) {
      setTruncatedText(description);
      return;
    }

    let bestFit = description;

    for (let i = terms.length; i >= 1; i--) {
      const visibleTerms = terms.slice(0, i);
      const hiddenTerms = terms.slice(i);
      let hiddenCount = hiddenTerms.length;

      // Handle existing hardcoded counts like "Diesel (+2)"
      for (const hiddenTerm of hiddenTerms) {
        const match = /\(\+(\d+)\)/.exec(hiddenTerm);
        if (match) {
          const hardcodedCount = Number.parseInt(match[1], 10);
          // Add the hardcoded count to the total (the term itself + its represented count)
          hiddenCount = hiddenCount + hardcodedCount;
        }
      }

      let testText = visibleTerms.join(` ${separator} `);
      if (hiddenCount > 0) {
        testText += ` ${separator} (+${hiddenCount})`;
      }

      const textWidth = getTextWidths([testText], '12px Lato')[0];

      if (textWidth <= containerWidth) {
        bestFit = testText;
        break;
      }
    }

    setTruncatedText(bestFit);
  }, [description, separator, containerRef]);

  // Update on description change and resize
  useEffect(() => {
    const handleUpdate = () => {
      // Wait for layout to complete before measuring DOM elements
      requestAnimationFrame(updateTruncatedText);
    };

    handleUpdate();

    window.addEventListener('resize', handleUpdate);
    return () => window.removeEventListener('resize', handleUpdate);
  }, [updateTruncatedText]);

  return {
    truncatedText,
  };
};

export { useTruncatedTerms };
